import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import {
  NoteListItem,
  NoteListGroupHeader,
  NoteListGroupFooter,
  NoteDetail,
  RepayScheduleListItem,
  DisbursementScheduleListItem
} from '../models/note';

@Injectable({
  providedIn: 'root'
})
export class NoteService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('NoteService');
  }

  public getNoteList(participantId: number): Observable<NoteListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Note/NoteList';
    if (this._logging.serviceCallTraceLog) {
      console.log('NoteService.getNoteList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<NoteListItem>>(serviceUrl, {participantId: participantId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getNoteList', []))
    );
  }

  public addNoteListHeadersFooters(noteList: NoteListItem[]): (NoteListItem | NoteListGroupHeader | NoteListGroupFooter)[] {
    const updatedNoteList: (NoteListItem | NoteListGroupHeader | NoteListGroupFooter)[] = [];

    let currentState = null;
    let cpbTotal = 0;
    let pastDueAmountTotal = 0;
    let totalAmountDueTotal = 0;

    for (const noteListItem of noteList) {
      if (noteListItem.state !== currentState) {
        if (currentState !== null) {
          updatedNoteList.push(this._getNewNoteListGroupFooter(cpbTotal, pastDueAmountTotal, totalAmountDueTotal));
        }
        currentState = noteListItem.state;
        updatedNoteList.push(this._getNewNoteListGroupHeader(noteListItem.state.toUpperCase()));
        cpbTotal = 0;
        pastDueAmountTotal = 0;
        totalAmountDueTotal = 0;
      }
      updatedNoteList.push(noteListItem);
      cpbTotal += noteListItem.cpb;
      pastDueAmountTotal += noteListItem.pastDueAmount;
      totalAmountDueTotal += noteListItem.totalAmountDue;
    }
    if (currentState !== null) {
      updatedNoteList.push(this._getNewNoteListGroupFooter(cpbTotal, pastDueAmountTotal, totalAmountDueTotal));
    }
    return updatedNoteList;
  }

  private _getNewNoteListGroupHeader(headerTitle: string = ''): NoteListGroupHeader {
    let noteListGroupHeader: NoteListGroupHeader;
    noteListGroupHeader = {
      isGroupHeader: true,
      headerTitle: headerTitle
    };
    return noteListGroupHeader;
  }

  private _getNewNoteListGroupFooter(cpbTotal: number, pastDueAmountTotal: number, totalAmountDueTotal: number): NoteListGroupFooter {
    let noteListGroupFooter: NoteListGroupFooter;
    noteListGroupFooter = {
      isGroupFooter: true,
      cpbTotal: cpbTotal,
      pastDueAmountTotal: pastDueAmountTotal,
      totalAmountDueTotal: totalAmountDueTotal
    };
    return noteListGroupFooter;
  }

  public getNoteDetail(noteId: number): Observable<NoteDetail> {
    const serviceUrl: string = this._baseServiceUrl + 'Note/NoteDetail';
    if (this._logging.serviceCallTraceLog) {
      console.log('NoteService.getNoteDetail :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<NoteDetail>(serviceUrl, {noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getNoteDetail', null))
    );
  }

  public getNoteRepayScheduleList(noteId: number): Observable<RepayScheduleListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Note/RepayScheduleList';
    if (this._logging.serviceCallTraceLog) {
      console.log('NoteService.getNoteRepayScheduleList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<RepayScheduleListItem>>(serviceUrl, {noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getNoteRepayScheduleList', []))
    );
  }

  public getNoteDisbursementScheduleList(noteId: number): Observable<DisbursementScheduleListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Note/DisbursementScheduleList';
    if (this._logging.serviceCallTraceLog) {
      console.log('NoteService.getNoteDisbursementScheduleList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<DisbursementScheduleListItem>>(serviceUrl, {noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getNoteDisbursementScheduleList', []))
    );
  }
}
